import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Plus, Trash, Key } from "@phosphor-icons/react";

const ROLE_LABEL = { admin: "Admin", staff: "Staff (CRM)", finance: "Finance (Bills)" };
const ROLE_BADGE = { admin: "bg-forest text-cream", staff: "bg-sand text-forest", finance: "bg-terracotta text-cream" };

function AddStaffDialog({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "staff" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) { toast.error("All fields required"); return; }
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success(`${form.name} added`);
      setForm({ name: "", email: "", password: "", role: "staff" });
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add user");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="add-team-btn" className="inline-flex items-center gap-2 rounded-full bg-forest text-cream px-5 py-2.5 text-sm hover:bg-terracotta transition-all">
          <Plus size={16} weight="bold"/> Add team member
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md bg-cream" data-testid="team-dialog">
        <DialogHeader><DialogTitle className="font-serif-display text-2xl">Add a team member</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="team-form">
          <div><Label>Full name *</Label><Input data-testid="team-name" value={form.name} onChange={set("name")} className="bg-ivory h-11 mt-1" required/></div>
          <div><Label>Email *</Label><Input data-testid="team-email" type="email" value={form.email} onChange={set("email")} className="bg-ivory h-11 mt-1" required/></div>
          <div><Label>Password *</Label><Input data-testid="team-password" type="text" value={form.password} onChange={set("password")} className="bg-ivory h-11 mt-1" minLength={6} required placeholder="min 6 characters"/></div>
          <div>
            <Label>Role</Label>
            <Select value={form.role} onValueChange={(v) => set("role")(v)}>
              <SelectTrigger data-testid="team-role" className="bg-ivory h-11 mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="staff">Staff — CRM (visits, follow-ups)</SelectItem>
                <SelectItem value="finance">Finance — Bills of Entry only</SelectItem>
                <SelectItem value="admin">Admin — full access</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-2">
              <b>Staff</b>: sees the CRM (visits, bookings, follow-ups) but cannot see or create bills. <br/>
              <b>Finance</b>: sees only the Bills of Entry page — no CRM, no contacts, no site content. <br/>
              <b>Admin</b>: everything.
            </p>
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setOpen(false)} className="rounded-full border border-border px-5 py-2 text-sm">Cancel</button>
            <button type="submit" disabled={saving} data-testid="team-submit" className="rounded-full bg-forest text-cream px-6 py-2.5 text-sm hover:bg-terracotta transition-all disabled:opacity-50">
              {saving ? "Adding…" : "Add member"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ user, onSaved }) {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      await api.patch(`/users/${user.id}`, { password: pw });
      toast.success("Password reset");
      setPw("");
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="text-xs text-forest hover:text-terracotta inline-flex items-center gap-1" data-testid={`reset-${user.id}`}>
          <Key size={14}/> Reset password
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-sm bg-cream">
        <DialogHeader><DialogTitle className="font-serif-display text-2xl">Reset password</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-muted-foreground">Set a new password for <b>{user.name}</b> ({user.email}).</p>
          <Input type="text" value={pw} onChange={(e) => setPw(e.target.value)} className="bg-ivory h-11" placeholder="New password (min 6 chars)" required/>
          <DialogFooter>
            <button type="button" onClick={() => setOpen(false)} className="rounded-full border border-border px-5 py-2 text-sm">Cancel</button>
            <button type="submit" disabled={saving} className="rounded-full bg-forest text-cream px-6 py-2.5 text-sm hover:bg-terracotta disabled:opacity-50">{saving ? "Saving…" : "Reset"}</button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminTeam() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setRows(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name}? They will lose access immediately.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="admin-team">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <h1 className="font-serif-display text-4xl">Team access</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Give your sales reps and receptionists view-only access to the CRM — they can see and export visits, but cannot add, edit or delete anything.
          </p>
        </div>
        <AddStaffDialog onSaved={load}/>
      </div>

      <div className="mt-8 bg-ivory border border-border/50 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="p-4">Name</th>
              <th className="p-4">Email</th>
              <th className="p-4">Role</th>
              <th className="p-4">Added</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="p-10 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && rows.map(u => {
              const isMe = u.id === me?.id;
              return (
                <tr key={u.id} className="border-t border-border/40" data-testid={`team-row-${u.id}`}>
                  <td className="p-4 font-medium">{u.name} {isMe && <span className="text-xs text-muted-foreground">(you)</span>}</td>
                  <td className="p-4 text-muted-foreground">{u.email}</td>
                  <td className="p-4">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.15em] ${ROLE_BADGE[u.role] || ROLE_BADGE.staff}`}>
                      {ROLE_LABEL[u.role] || u.role}
                    </span>
                  </td>
                  <td className="p-4 text-xs text-muted-foreground">{new Date(u.created_at).toLocaleDateString()}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-4">
                      <ResetPasswordDialog user={u} onSaved={load}/>
                      {!isMe && (
                        <button onClick={() => remove(u)} className="text-xs text-destructive hover:underline inline-flex items-center gap-1" data-testid={`remove-${u.id}`}>
                          <Trash size={14}/> Remove
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-8 bg-sand/60 border border-border/50 rounded-2xl p-6 text-sm text-muted-foreground">
        <div className="font-medium text-forest mb-2">Role permissions</div>
        <ul className="list-disc pl-5 space-y-1">
          <li><b>Admin</b>: everything — CRM, contacts, testimonials, journal, site content, team, bills</li>
          <li><b>Staff</b>: CRM (visits, follow-ups, medicine, status) + export PDFs. No bills, no contacts, no site content.</li>
          <li><b>Finance</b>: Bills of Entry only — create, edit, receipts, monthly register. No CRM, no patient list.</li>
        </ul>
      </div>
    </div>
  );
}
